import React, { useCallback, useEffect, useState } from "react";
import {
  adminGetAnnouncements,
  adminSaveAnnouncement,
  adminDeleteAnnouncement,
} from "../../api/announcementApi";
import "./AdminConsultations.css"; // shared ac-* admin form/card styles
import "./AdminAnnouncements.css";

const empty = () => ({ id: null, text: "", linkUrl: "", isActive: true, sortOrder: 0, startsAt: "", endsAt: "" });

// <input type="datetime-local"> works in local time; the API stores ISO/UTC.
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const toIso = (local) => (local ? new Date(local).toISOString() : null);

const windowLabel = (a) => {
  const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  if (a.startsAt && a.endsAt) return `${fmt(a.startsAt)} → ${fmt(a.endsAt)}`;
  if (a.startsAt) return `From ${fmt(a.startsAt)}`;
  if (a.endsAt) return `Until ${fmt(a.endsAt)}`;
  return "Always";
};

const liveNow = (a) => {
  const now = Date.now();
  return a.isActive && (!a.startsAt || new Date(a.startsAt) <= now) && (!a.endsAt || new Date(a.endsAt) > now);
};

export default function AdminAnnouncements({ token }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const flash = (type, text) => {
    setNotice({ type, text });
    setTimeout(() => setNotice(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const result = await adminGetAnnouncements(token);
    if (result.ok && result.success) setList(result.data);
    else flash("error", result.message || "Unable to load announcements");
    setLoading(false);
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const open = (a) =>
    setForm(
      a
        ? { id: a.id, text: a.text, linkUrl: a.linkUrl || "", isActive: a.isActive, sortOrder: a.sortOrder, startsAt: toLocalInput(a.startsAt), endsAt: toLocalInput(a.endsAt) }
        : empty()
    );

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const result = await adminSaveAnnouncement(
      form.id,
      {
        text: form.text,
        linkUrl: form.linkUrl,
        isActive: form.isActive,
        sortOrder: form.sortOrder,
        startsAt: toIso(form.startsAt),
        endsAt: toIso(form.endsAt),
      },
      token
    );
    setSaving(false);
    if (result.ok && result.success) {
      flash("success", form.id ? "Announcement updated" : "Announcement added");
      setForm(null);
      load();
    } else {
      flash("error", result.message || "Unable to save announcement");
    }
  };

  const toggle = async (a) => {
    const result = await adminSaveAnnouncement(a.id, { isActive: !a.isActive }, token);
    if (result.ok && result.success) load();
    else flash("error", result.message || "Unable to update");
  };

  const remove = async (a) => {
    if (!window.confirm("Delete this announcement?")) return;
    const result = await adminDeleteAnnouncement(a.id, token);
    if (result.ok && result.success) {
      flash("success", "Announcement deleted");
      load();
    } else {
      flash("error", result.message || "Unable to delete");
    }
  };

  return (
    <div className="panel-tab ac-wrap">
      {notice && <div className={`feedback-alert ${notice.type}`}>{notice.text}</div>}

      {form ? (
        <form className="ac-form" onSubmit={save}>
          <h3>{form.id ? "Edit announcement" : "Add announcement"}</h3>

          <label>
            Message * <small className="an-count">{form.text.length}/300</small>
            <input className="ac-input" required maxLength={300} value={form.text} onChange={(e) => set("text", e.target.value)} placeholder="Flat 20% OFF on all Rudraksha - use code RUDRA20" />
          </label>

          <label>
            Link (optional)
            <input className="ac-input" value={form.linkUrl} onChange={(e) => set("linkUrl", e.target.value)} placeholder="/shop/rudraksha  or  https://..." />
          </label>

          <div className="ac-grid">
            <label>Show from (optional)<input className="ac-input" type="datetime-local" value={form.startsAt} onChange={(e) => set("startsAt", e.target.value)} /></label>
            <label>Show until (optional)<input className="ac-input" type="datetime-local" value={form.endsAt} onChange={(e) => set("endsAt", e.target.value)} /></label>
            <label>Display order<input className="ac-input" type="number" value={form.sortOrder} onChange={(e) => set("sortOrder", e.target.value)} /></label>
          </div>

          <label className="ac-check">
            <input type="checkbox" checked={form.isActive} onChange={(e) => set("isActive", e.target.checked)} /> Show on the website
          </label>

          <div className="an-preview" aria-label="Preview">
            <span>{form.text || "Your message will look like this"}</span>
          </div>

          <div className="ac-actions">
            <button type="button" className="ac-btn" onClick={() => setForm(null)}>Cancel</button>
            <button type="submit" className="ac-btn ac-btn-primary" disabled={saving}>{saving ? "Saving..." : "Save"}</button>
          </div>
        </form>
      ) : (
        <>
          <div className="ac-bar">
            <button className="ac-btn ac-btn-primary" onClick={() => open(null)}>+ Add announcement</button>
          </div>

          {loading ? (
            <p>Loading...</p>
          ) : list.length === 0 ? (
            <p>No announcements yet. Add one and it will scroll across the top of every page.</p>
          ) : (
            <div className="an-list">
              {list.map((a) => (
                <div className={`an-row ${a.isActive ? "" : "is-off"}`} key={a.id}>
                  <div className="an-main">
                    <strong>{a.text}</strong>
                    <div className="user-email">
                      {windowLabel(a)}
                      {a.linkUrl ? ` · ${a.linkUrl}` : ""}
                    </div>
                    <span className={`an-status ${liveNow(a) ? "is-live" : ""}`}>
                      {liveNow(a) ? "Live now" : a.isActive ? "Scheduled / expired" : "Hidden"}
                    </span>
                  </div>
                  <div className="ac-actions">
                    <button className="ac-btn" onClick={() => toggle(a)}>{a.isActive ? "Hide" : "Show"}</button>
                    <button className="ac-btn" onClick={() => open(a)}>Edit</button>
                    <button className="ac-btn ac-btn-danger" onClick={() => remove(a)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
